import { Client, xml } from "@xmpp/client"
import posthog from "posthog-js"
import { Element } from "@xmpp/xml"
import { JID } from "@xmpp/jid"
import {
  archiveQueryResult,
  parseArchivedTimestamp,
  parseStanza,
} from "../lib/stanzaParser"
import { handleSearchResultStanza } from "../lib/streamedSearchCollector"
import type { AppDispatch, AppStore } from "../reducers/store"
import MessageParser from "../lib/messageParser"
import MarkerParser from "../lib/markerParser"
import {
  addBatchDeliveryOrders,
  addBatchDocumentDriverContext,
  addBatchDocumentLinks,
  addBatchExtractedMetadata,
  addBatchFastens,
  addBatchMessageFileShares,
  addBatchMessageReactions,
  addMessageRetraction,
  markMessage,
  type Message,
} from "../reducers/messagesSlice"
import {
  addOrUpdateParticipant,
  buildChat,
  updateChatName,
} from "../reducers/chatsSlice"
import { DriverLocation, upsertLocation } from "../reducers/locationsSlice"
import {
  removeTrailerLocation,
  TrailerLocation,
  upsertTrailerLocation,
} from "../reducers/trailerLocationsSlice"
import { updateConnectionStatus } from "../reducers/connectionStatusSlice"
import {
  joinChat,
  leaveChat,
  processIncomingMessage,
} from "../reducers/chatsSliceThunks"
import { IncomingContext } from "@xmpp/middleware"
import { setUpdateAvailable } from "../reducers/appVersionSlice"
import { updateSettings } from "../reducers/settingsSlice"
import { upsertChatSubject } from "../reducers/subjectsSlice"
import { removeRoute, upsertRoute } from "../reducers/routesSlice"
import { upsertActivity } from "../reducers/activitySlice"
import PresenceParser from "../lib/presenceParser"
import XmppAnalytics from "./xmppAnalytics"
import ReactionsParser from "../lib/reactionsParser"
import MessageFileSharedParser from "../lib/messageFileSharedParser"
import ReconnectStatusReactor from "../lib/reconnectStatusReactor"
import { captureError } from "../ErrorHandlers"
import { storeMyJid } from "../lib/cookieStoredJidStorage"
import XmppGroupChatApi from "../api/xmppGroupChatApi"
import { communicatorJid } from "../jidUtils"
import { resolveDriverJidForChat } from "../lib/resolveDriverForChat"
import XmppVCardApi from "../api/xmppVCardApi"
import { Profile, upsertProfile } from "../reducers/profilesSlice"
import { Route2g, upsertRoute2g } from "../reducers/routes2gSlice"
import { addBookmark } from "../api/xeps/PepNativeBookmarks"
import { iqInfo } from "../api/xeps/ServiceDiscovery"
import { selectChatJidByDriverJid } from "../reducers/chatsSliceSelectors"
import { modifyContact as modifyCommunicatorContact } from "../reducers/communicatorContactsSlice"
import FastenParser from "../lib/fastenParser"
import { pinMessage, unpinMessage } from "../reducers/pinnedSlice"
import { Tacho, upsertTacho } from "../reducers/tachosSlice"
import {
  DeliveryOrderExportStatus,
  upsertDeliveryOrderExportStatus,
  removeDeliveryOrderExportStatus,
} from "../reducers/deliveryOrderExportStatusesSlice"
import {
  ReferenceRouteStatus,
  removeReferenceRouteStatus,
  upsertReferenceRouteStatus,
} from "../reducers/referenceRouteStatusesSlice"
import { shouldApplyAgentChatState } from "../lib/agentThread"
import { isAgentForDriverPerChatEnabled } from "@/hooks/useAgentForDriverPerChat"
import { updateRouteAlertConfig } from "../reducers/routes2gSlice"
import { RouteAlertConfigWithJid } from "../api/xeps/RouteAlertConfigs"
import ChatSettingsPubSubEventParser from "@/lib/chatSettingsPubSubEventParser"
import {
  addChatSettings,
  ChatSettings,
  removeChatSettings,
} from "@/reducers/chatsSettingsSlice"
import DeliveryOrderParser from "@/lib/deliveryOrderParser"
import GpsConfigPubSubEventParser from "@/lib/gpsConfigPubSubEventParser"
import {
  addGpsConfig,
  GpsConfig,
  removeGpsConfig,
} from "@/reducers/gpsConfigSlice"
import CarPubSubEventParser from "@/lib/carPubSubEventParser"
import { addCar, Car, removeCar } from "@/reducers/carsSlice"
import {
  WorkflowPubSubEventParser,
  WorkflowPubSubItem,
} from "@/lib/workflowPubSubEventParser"
import {
  upsertItemWorkflows,
  removeItemWorkflows,
} from "@/reducers/openclawWorkflowsSlice"
import ExtractedMetadataParser from "@/lib/extractedMetadataParser"
import DocumentDriverContextParser from "@/lib/documentDriverContextParser"
import DocumentLinkParser from "@/lib/documentLinkParser"
import stanzaVolumeMonitor from "../instrumentation/stanzaVolumeMonitor"
import { parseRttStanza } from "../lib/rttParser"
import { rttNew, rttAppend, rttReset } from "../reducers/rttSlice"
import ChatHelpers from "../lib/chatHelpers"
import { parseAgentStep } from "../lib/agentStepParser"
import {
  agentSignal,
  type AgentChatState,
} from "../reducers/agentPipelineSlice"
import { turnAnswered } from "../reducers/agentTurnActions"
import { pushAiWidgetMap } from "@/reducers/aiWidgetMapSlice"
import { handleAccountConfigEvents } from "./accountConfigEventHandler"
import i18n from "@/lib/i18n/i18n"

const isAIStreamingEnabled = () =>
  window.anonymousXMPP === true ||
  posthog.isFeatureEnabled("ai-streaming") === true

const agentChatState = (message: Message): AgentChatState =>
  message.composing ? "composing" : message.paused ? "paused" : "active"

const XmppEventHandlers = {
  handleOnStanza: (store: AppStore, xmpp: Client) => {
    const { dispatch } = store

    return async (stanza: Element) => {
      if (stanza.attrs.type === "error") {
        stanzaVolumeMonitor.record("error")
        return
      }

      // Intercept streamed search result messages before any other processing.
      // These carry individual hits from the search_streamed ad-hoc command
      // and must not enter the normal message pipeline.
      if (handleSearchResultStanza(stanza)) {
        stanzaVolumeMonitor.record("search_result")
        return
      }

      if (
        handleAccountConfigEvents(stanza, dispatch, xmpp.jid?.bare().toString())
      ) {
        stanzaVolumeMonitor.record("account_config_pubsub_event")
        return
      }

      const result = parseStanza(stanza, xmpp.jid?.bare().toString())

      if (!result) {
        stanzaVolumeMonitor.record("unknown")
        return
      }

      const { stanza: messageStanza } = result

      stanzaVolumeMonitor.record(result.type)

      if (!xmpp.jid) return
      const occupantJid = archiveQueryResult(stanza)
        ? undefined
        : window.mucPlugin?.occupantJid

      if (result.type === "message") {
        const message = MessageParser.parse(messageStanza, {
          timestamp: parseArchivedTimestamp(stanza),
          occupantJid,
        })

        dispatch(
          processIncomingMessage(
            { message, myJid: xmpp.jid.toString() },
            xmpp,
            result.type,
          ),
        )

        // A final <body> message from SlickAI ends the turn and its draft.
        const fromJid = messageStanza.getAttr("from") ?? ""
        if (ChatHelpers.isMessageFromSlickAI(fromJid)) {
          const roomJid = fromJid.split("/")[0]
          if (roomJid) {
            dispatch(
              turnAnswered({ roomJid, messageId: message.id, at: Date.now() }),
            )
          }
        }

        // Also check for delivery order data
        const deliveryOrder = DeliveryOrderParser.parse(
          messageStanza,
          parseArchivedTimestamp(stanza),
        )
        if (deliveryOrder) {
          dispatch(addBatchDeliveryOrders({ deliveryOrders: [deliveryOrder] }))
        }

        return
      }

      if (result.type === "ai_widget_map") {
        // MAM-result wrappers arrive on both the global "stanza" listener
        // (this one) and MAM's own accumulator. We let MAMStanzaProcessor
        // handle the archived case so the map only goes into the slice for
        // truly live arrivals — keeps the "panel hidden until click" UX
        // after reload.
        if (archiveQueryResult(stanza)) {
          return
        }
        const message = MessageParser.parse(messageStanza, {
          timestamp: parseArchivedTimestamp(stanza),
        })
        message.aiWidgetMap = result.parsed
        dispatch(
          processIncomingMessage(
            { message, myJid: xmpp.jid.toString() },
            xmpp,
            "message",
          ),
        )
        dispatch(pushAiWidgetMap(result.parsed))
        return
      }

      if (result.type === "chat_state") {
        const message = MessageParser.parse(messageStanza, {
          timestamp: parseArchivedTimestamp(stanza),
        })
        // Both halves of what the agent shows while it works — the composing
        // line and the pipeline steps — ride on this stanza, so the stamp
        // decides both.
        const showAgentProgress = shouldApplyAgentChatState(
          message,
          isAgentForDriverPerChatEnabled(),
        )
        if (
          showAgentProgress &&
          ChatHelpers.isMessageFromSlickAI(message.from)
        ) {
          dispatch(
            agentSignal({
              roomJid: message.from.split("/")[0],
              at: Date.now(),
              chatState: agentChatState(message),
              activity: message.activity,
              step: parseAgentStep(result.stanza),
            }),
          )
        }

        return
      }

      if (result.type === "rtt") {
        if (!isAIStreamingEnabled()) {
          return
        }

        const rttEvent = parseRttStanza(result.stanza)
        if (rttEvent) {
          switch (rttEvent.event) {
            case "new":
              dispatch(
                rttNew({ roomJid: rttEvent.roomJid, text: rttEvent.text }),
              )
              break
            case "append":
              dispatch(
                rttAppend({
                  roomJid: rttEvent.roomJid,
                  text: rttEvent.text,
                  seq: rttEvent.seq,
                }),
              )
              break
            case "reset":
              dispatch(
                rttReset({
                  roomJid: rttEvent.roomJid,
                  text: rttEvent.text,
                  seq: rttEvent.seq,
                }),
              )
              break
          }
        }
        return
      }

      if (result.type === "presence") {
        const presence = PresenceParser.parse(stanza)
        if (!presence) return

        if (presence.selfPresence) {
          if (
            presence.type === "disconnected" &&
            // Presence stanzas related to leaving a chat are in 300+ range
            // https://xmpp.org/extensions/xep-0045.html#registrar-statuscodes-init
            presence.statuses.some((status) => status >= 300)
          ) {
            XmppAnalytics.trackOnChatDisconnected({
              userJid: xmpp.jid.toString(),
              chatJid: presence.from.toString(),
              statuses: presence.statuses,
            })
          }

          // dispatch(
          //   updateChatConnectedStatus({
          //     jid: presence.chat,
          //     status: presence.type,
          //   }),
          // )
        }

        if (presence.shouldUpdateVcard) {
          const jid = presence.from.bare().toString()
          const vcard = await XmppVCardApi.fetchVCard(window.XMPP!, jid)
          if (!vcard) return

          dispatch(upsertProfile({ profile: vcard }))

          const roomJid = selectChatJidByDriverJid(jid)(store.getState())
          if (roomJid) {
            dispatch(
              addOrUpdateParticipant({ chatJid: roomJid, participant: vcard }),
            )
          }
        }
      }

      if (result.type === "chat_marker") {
        const marker = MarkerParser.parse(messageStanza, {
          timestamp: parseArchivedTimestamp(stanza),
        })
        dispatch(markMessage({ marker, myJid: xmpp.jid.toString() }))
      }

      if (result.type === "route") {
        dispatch(upsertRoute({ route: result.parsed }))
      }

      if (result.type === "route2g") {
        const route = result.parsed
        if (route.type === "create" && route.item) {
          dispatch(upsertRoute2g({ route: route.item as Route2g }))
        }
      }

      if (result.type === "tacho") {
        const tacho = result.parsed
        if (tacho.type === "create" && tacho.item) {
          dispatch(upsertTacho({ tacho: tacho.item as Tacho }))
        }
      }

      if (result.type === "delivery_order_export_status") {
        const exportStatus = result.parsed
        if (exportStatus.type === "create" && exportStatus.item) {
          dispatch(
            upsertDeliveryOrderExportStatus({
              exportStatus: exportStatus.item as DeliveryOrderExportStatus,
            }),
          )
        } else if (exportStatus.type === "retract") {
          dispatch(
            removeDeliveryOrderExportStatus({
              roomJid: (exportStatus.item as { id: string }).id,
            }),
          )
        }
      }

      if (result.type === "user_vcard") {
        const user = result.parsed
        if (user.type === "create" && user.item) {
          dispatch(upsertProfile({ profile: user.item as Profile }))
        }
      }

      if (result.type === "reference_route_status") {
        const routeStatus = result.parsed

        if (routeStatus.type === "create" && routeStatus.item) {
          dispatch(
            upsertReferenceRouteStatus({
              referenceRouteStatus: routeStatus.item as ReferenceRouteStatus,
            }),
          )
        } else if (routeStatus.type === "retract") {
          dispatch(
            removeReferenceRouteStatus({
              jid: (routeStatus.item as { id: string }).id,
            }),
          )
        }
      }

      if (result.type === "activity") {
        dispatch(upsertActivity({ activity: result.parsed }))
      }

      if (result.type === "remove_route") {
        dispatch(removeRoute({ jid: stanza.attrs.from }))
      }

      if (result.type === "geoloc") {
        const geoloc = result.parsed
        if (geoloc.type === "create" && geoloc.item) {
          dispatch(
            upsertLocation({
              driverLocation: geoloc.item as DriverLocation,
            }),
          )
        } else if (geoloc.type === "retract") {
          dispatch(
            upsertLocation({
              driverLocation: {
                jid: (geoloc.item as { id: string }).id,
                location: undefined,
              },
            }),
          )
        }
      }

      if (result.type === "trailer_geoloc") {
        const event = result.parsed
        if (event.type === "create" && event.item) {
          dispatch(
            upsertTrailerLocation({
              trailerLocation: event.item as TrailerLocation,
            }),
          )
        } else if (event.type === "retract") {
          dispatch(
            removeTrailerLocation({ id: (event.item as { id: string }).id }),
          )
        }
      }

      if (result.type === "settings") {
        dispatch(updateSettings({ settings: result.parsed }))
      }

      if (result.type === "subject") {
        dispatch(upsertChatSubject({ subject: result.parsed }))
      }

      if (result.type === "bookmark_event") {
        const event = result.parsed

        if (event.from == communicatorJid(xmpp.jid)) {
          const jid = event.type === "create" ? event.bookmark.jid : event.jid
          const name = event.type === "create" ? event.bookmark.name : undefined

          return dispatch(
            modifyCommunicatorContact({
              type: event.type,
              jid: jid,
              name: name,
            }),
          )
        }

        if (event.type === "create") {
          dispatch(
            joinChat(xmpp, {
              jid: event.bookmark.jid,
              name: event.bookmark.name || event.bookmark.jid,
              room: true,
            }),
          )
        } else if (event.type === "retract") {
          dispatch(
            leaveChat(
              xmpp,
              buildChat({
                jid: event.jid,
              }),
            ),
          )
        }
      }

      if (result.type === "chat_settings_pubsub_event") {
        const event = ChatSettingsPubSubEventParser.parse(stanza)
        if (!event) return

        if (event.type === "create") {
          dispatch(
            addChatSettings({
              chatSettings: event.chatSettings as ChatSettings,
            }),
          )
        } else if (event.type === "retract") {
          dispatch(removeChatSettings({ chatJid: event.chatSettings.chatJid }))
        }
      }

      if (result.type === "gps_config_pubsub_event") {
        const event = GpsConfigPubSubEventParser.parse(stanza)
        if (!event) return

        if (event.type === "create") {
          dispatch(
            addGpsConfig({
              gpsConfig: event.gpsConfig as GpsConfig,
            }),
          )
        } else if (event.type === "retract") {
          dispatch(removeGpsConfig({ driverJid: event.gpsConfig.driverJid }))
        }
      }

      if (result.type === "workflow_pubsub_event") {
        const event = WorkflowPubSubEventParser.parse(stanza)
        if (!event) return

        if (event.type === "create" && event.item) {
          const { itemId, workflows } = event.item as WorkflowPubSubItem
          dispatch(upsertItemWorkflows({ itemId, workflows }))
        } else if (event.type === "retract" && event.item) {
          dispatch(
            removeItemWorkflows({
              itemId: (event.item as { id: string }).id,
            }),
          )
        }
      }

      if (result.type === "car_pubsub_event") {
        const event = CarPubSubEventParser.parse(stanza)
        if (!event) return

        if (event.type === "create") {
          dispatch(
            addCar({
              car: event.car as Car,
            }),
          )
        } else if (event.type === "retract") {
          dispatch(removeCar({ id: event.car.id }))
        }
      }

      if (result.type === "route_alert_config") {
        const alertConfig = result.parsed

        if (alertConfig.type === "create" && alertConfig.item) {
          const item = alertConfig.item as RouteAlertConfigWithJid
          const { jid, ...config } = item
          dispatch(
            updateRouteAlertConfig({
              jid,
              config,
            }),
          )
        }
      }

      if (result.type === "reaction") {
        const reaction = ReactionsParser.parse(
          stanza,
          parseArchivedTimestamp(stanza),
          { occupantJid },
        )
        if (!reaction) return

        dispatch(
          addBatchMessageReactions({
            reactions: [reaction],
            myJid: xmpp.jid.toString(),
          }),
        )
      }

      if (result.type === "pinned_message_event") {
        const event = result.parsed

        if (event.type === "create") {
          dispatch(
            pinMessage({
              message: event.pinned.message,
              itemId: event.pinned.itemId,
              at: event.pinned.at,
              ttl: event.pinned.ttl,
            }),
          )
        } else if (event.type === "retract") {
          dispatch(unpinMessage({ itemId: event.id }))
        }
      }

      if (result.type === "message_file_shared") {
        const messageFileShared = MessageFileSharedParser.parse(
          stanza,
          parseArchivedTimestamp(stanza),
        )
        if (!messageFileShared) return

        dispatch(
          addBatchMessageFileShares({ messageFileShares: [messageFileShared] }),
        )
      }

      if (result.type === "extracted_metadata") {
        const extractedMetadata = ExtractedMetadataParser.parse(
          stanza,
          parseArchivedTimestamp(stanza),
        )
        if (!extractedMetadata) return

        dispatch(
          addBatchExtractedMetadata({ extractedMetadata: [extractedMetadata] }),
        )
      }

      if (result.type === "document_driver_context") {
        const driverContext = DocumentDriverContextParser.parse(
          stanza,
          parseArchivedTimestamp(stanza),
        )
        if (!driverContext) return

        dispatch(
          addBatchDocumentDriverContext({
            documentDriverContexts: [driverContext],
          }),
        )
      }

      if (result.type === "document_link") {
        const documentLink = DocumentLinkParser.parse(
          stanza,
          parseArchivedTimestamp(stanza),
        )
        if (!documentLink) return

        dispatch(addBatchDocumentLinks({ documentLinks: [documentLink] }))
      }

      if (result.type === "fasten") {
        const fasten = FastenParser.parse(
          stanza,
          parseArchivedTimestamp(stanza),
        )
        if (!fasten) return

        dispatch(addBatchFastens({ fastens: [fasten] }))
      }

      if (result.type === "retraction") {
        dispatch(
          addMessageRetraction({
            retraction: result.parsed,
            myJid: xmpp.jid.toString(),
          }),
        )
      }

      if (result.type === "muc_invitation") {
        const roomInfo = await iqInfo(xmpp, result.parsed.jid)
        const identity = roomInfo.identities[0]
        const roomName = identity.name as string

        addBookmark(xmpp, result.parsed.jid, roomName)
        window.analytics.track("AddBookmark", {
          chatJid: result.parsed.jid,
          chatName: roomName,
          from: "MUC Invitation",
        })
      }
    }
  },

  handleOnOnline: async (xmpp: Client, store: AppStore, jid: JID) => {
    store.dispatch(updateConnectionStatus({ status: "online" }))
    storeMyJid(jid.toString())
    window.myJID = jid
    window.appToaster.clear()
  },

  handleOnOffline: (dispatch: AppDispatch) => {
    return () => {
      dispatch(updateConnectionStatus({ status: "offline" }))
    }
  },

  handleOnDisconnect: (dispatch: AppDispatch) => {
    return () => {
      dispatch(updateConnectionStatus({ status: "disconnected" }))
    }
  },

  handleConnectionStatusChange: (client: Client) => {
    return (status: string) => {
      // Check for jid on XMPP so that we are not showing toaster when user trying to login and entered wrong password
      if (client?.jid && status === "disconnect") {
        window.appToaster.show(
          {
            message: i18n.t("app.connection.lost"),
            intent: "danger",
            timeout: 0,
          },
          "connection",
        )
      }
    }
  },

  handleReconnectStatusChange: (status: string) => {
    ReconnectStatusReactor.handleReconnectStatusChange(status)
  },

  handleNewClientVersionCommand: (dispatch: AppDispatch) => {
    return (context: IncomingContext<Client>) => {
      if (
        context.stanza.getChild("command")?.attrs.node === "new_client_version"
      ) {
        dispatch(setUpdateAvailable({ updateAvailable: true }))
        return xml("command", {
          xmlns: "http://jabber.org/protocol/commands",
          node: "new_client_version",
          status: "completed",
        })
      }
    }
  },

  handleRoomConfigChange: async (
    client: Client,
    roomJid: string,
    dispatch: AppDispatch,
    getState?: () => any,
  ) => {
    const roomConfig = await XmppGroupChatApi.fetchRoomConfig(client, roomJid)
    if (roomConfig && roomConfig.name) {
      dispatch(updateChatName({ chatJid: roomJid, name: roomConfig?.name }))
    }

    const accountChat = getState?.()?.accountChats?.[roomJid]
    const jid = resolveDriverJidForChat(accountChat, roomJid)
    if (!jid) return

    const vcard = await XmppVCardApi.fetchVCard(client, jid)
    if (vcard) {
      dispatch(upsertProfile({ profile: vcard }))
      dispatch(addOrUpdateParticipant({ chatJid: roomJid, participant: vcard }))
    }
  },
}

export default XmppEventHandlers
